import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, MenuItem } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import useTenant from "../../../hooks/useTenant";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";
import FilterReset from "../../../components/FilterReset";
import {
  searchPayload,
  convertedValue,
  invoiceStatusOptions,
  formatDate,
} from "../../../utils/common";
import { OperatorConstants } from "../../../constants/operator.constants";
import ReactHookFormSelect from "../../../components/ReactHookFormSelect";
import CustomTextField from "../../../components/CustomTextField";
import { useDialog } from "../../../contexts/DialogContext";
import usePlan from "../../../hooks/usePlan";
import CustomDatePicker from "../../../components/CustomDatePicker";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const statusOptions = [
  {
    label: "Payment Pending",
    value: "PP",
  },
  {
    label: "Un Paid",
    value: "UP",
  },
  {
    label: "Paid",
    value: "PA",
  },
  {
    label: "Canelled",
    value: "CA",
  },
];

const AdvanceFilter = (props) => {
  const { unSetDialog } = useDialog();
  const today = formatDate(new Date());

  const initialValues = {
    userName: "",
    fullName: "",
    tenantName: [],
    expirationStr: "",
    expiration: [],
    status: [],
    invoiceDate: [new Date(), new Date()],
    invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
    planName: [],
  };

  const { fields, payload, setPayload, setFilterResetFunc, filterFormData, setFilterFormData } =
    props;

  const { autoSearchTenant, tenantAutoSearchResults } = useTenant();
  const { autoSearchPlan, planAutoSearchResults, isLoadingAutoSearch } = usePlan();

  const [planPayload, setPlanPayload] = useState(currentPayload);
  const [planOptions, setPlanOptions] = useState([]);
  const [tenantPayload, setTenantPayload] = useState(currentPayload);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [defaultTenant, setDefaultTenant] = useState([]);
  const [defaultStatus, setDefaultStatus] = useState([]);
  const [defaultPlan, setDefaultPlan] = useState([]);

  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: filterFormData || initialValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = methods;

  useEffect(() => {
    setFilterResetFunc(() => () => reset(initialValues));
  }, [reset]);

  const onSubmit = async (data) => {
    try {
      setFilterFormData(data);
      const controlledFields = [
        "userName",
        "fullName",
        "expiration",
        "planId",
        "tenantId",
        "invoiceStatus",
        "invoiceDate",
      ];
      let filters = payload["filters"].filter((f) => !controlledFields.includes(f.fieldName));

      if (data.userName != null && Object.keys(data.userName).length != 0) {
        filters.push({
          fieldName: "userName",
          fieldValue: data.userName,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: true,
          entityName: "RadUserEntity",
          attributeName: "radUser",
        });
      }
      if (data.fullName != null && Object.keys(data.fullName).length != 0) {
        filters.push({
          fieldName: "fullName",
          fieldValue: data.fullName,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: true,
          entityName: "RadUserEntity",
          attributeName: "radUser",
        });
      }
      if (data.expiration != "" && data.expiration != null) {
        filters.push({
          fieldName: "expiration",
          fieldValue: data.expirationStr,
          op: "btn",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (data.planName != null && Object.keys(data.planName).length != 0) {
        filters.push({
          fieldName: "planId",
          fieldValue: convertedValue(data.planName, "value").join(","),
          op: "eq",
          dataType: "int",
          condition: "and",
          isJoin: true,
          entityName: "PlanEntity",
          attributeName: "plan",
        });
      }
      if (data.tenantName != null && Object.keys(data.tenantName).length != 0) {
        filters.push({
          fieldName: "tenantId",
          fieldValue: convertedValue(data.tenantName, "value").join(","),
          op: "eq",
          dataType: "int",
          condition: "and",
          isJoin: true,
          entityName: "TenantEntity",
          attributeName: "tenant",
        });
      }
      if (data.status != null && Object.keys(data.status).length != 0) {
        filters.push({
          fieldName: "invoiceStatus",
          fieldValue: convertedValue(data.status, "value").join(","),
          op: "in",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (data.invoiceDate != "" && data.invoiceDate != null) {
        filters.push({
          fieldName: "invoiceDate",
          fieldValue: data.invoiceDateStr,
          op: "btn",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }

      setPayload({
        ...payload,
        filters: filters,
      });
      unSetDialog("advance_filter");
    } catch (err) {
      console.error(err, "errerrerrerr");
    }
  };

  const fetchTenant = async () => {
    await autoSearchTenant(tenantPayload);
  };
  useEffect(() => {
    fetchTenant();
  }, [tenantPayload]);

  useEffect(() => {
    if (tenantAutoSearchResults.data.length !== 0) {
      setTenantOptions([...tenantOptions, ...tenantAutoSearchResults.data]);
    }
  }, [tenantAutoSearchResults]);

  const fetchPlan = async () => {
    await autoSearchPlan(planPayload);
  };
  useEffect(() => {
    fetchPlan();
  }, [planPayload]);

  useEffect(() => {
    if (planAutoSearchResults.data.length !== 0) {
      setPlanOptions([...planOptions, ...planAutoSearchResults.data]);
    }
  }, [planAutoSearchResults]);

  const searchPlanInputChange = (data) => {
    let filters = [];
    filters.push({
      fieldName: "planName",
      fieldValue: data,
      op: "like",
      dataType: "string",
      condition: "and",
      isJoin: false,
    });
    setPlanPayload({
      ...planPayload,
      filters: filters,
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
    });
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={6} pt={4}>
            <Grid item md={4} sm={6} xs={12}>
              <CustomTextField
                label={t("user_name")}
                name="userName"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
            <Grid item md={4} sm={6} xs={12}>
              <CustomTextField
                label={t("full_name")}
                name="fullName"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>

            <Grid item md={4} sm={6} xs={12}>
              <SearchableAutoComplete
                control={control}
                data={tenantOptions}
                label={t("franchise_name")}
                payload={tenantPayload}
                setPayload={setTenantPayload}
                loading={isLoadingAutoSearch}
                name="tenantName"
                multiple={false}
                defaultValue={defaultTenant}
                hasNextPage={tenantAutoSearchResults.paging.hasNextPage}
              />
            </Grid>

            <Grid item md={4} sm={6} xs={12}>
              <CustomDatePicker
                name="expiration"
                nameTemp="expirationStr"
                label="Expiration Date"
                range={true}
                control={control}
              />
            </Grid>

            <Grid item md={4} sm={6} xs={12}>
              <SearchableAutoComplete
                control={control}
                data={invoiceStatusOptions()}
                label="Status"
                payload={null}
                setPayload={null}
                loading={false}
                name="status"
                multiple={true}
                defaultValue={defaultStatus}
              />
            </Grid>
            <Grid item md={4} sm={6} xs={12}>
              <CustomDatePicker
                name="invoiceDate"
                nameTemp="invoiceDateStr"
                label="Invoice Date"
                range={true}
                control={control}
              />
            </Grid>
            <Grid item md={4} sm={6} xs={12}>
              <SearchableAutoComplete
                control={control}
                data={planOptions}
                label="Plan Name"
                payload={planPayload}
                setPayload={setPlanPayload}
                loading={isLoadingAutoSearch}
                name="planName"
                multiple={false}
                defaultValue={defaultPlan}
                searchInputChange={searchPlanInputChange}
              />
            </Grid>
          </Grid>

          <FilterReset resetForm={() => reset(initialValues)} />
        </form>
      </FormProvider>
    </React.Fragment>
  );
};

export default AdvanceFilter;
